import { FormDefinition, FormElement } from '../../types/formSchema';
import { FORM_START_PAGE_ID } from './formFlowRuntime';

export interface FormDefinitionIssue {
  elementId?: string;
  message: string;
}

export const validateFormDefinitionStructure = (definition: FormDefinition): FormDefinitionIssue[] => {
  const issues: FormDefinitionIssue[] = [];
  const elements = definition.elements || [];
  const elementIds = new Set(elements.map(element => element.id));
  const questionIds = new Set(elements.filter(element => element.kind === 'QUESTION').map(element => element.id));
  const graph = new Map<string, string[]>();
  const pageIds = new Set([
    FORM_START_PAGE_ID,
    ...elements.filter(element => element.type === 'PAGE_BREAK').map(element => element.id)
  ]);
  const pageOrder = new Map<string, number>([[FORM_START_PAGE_ID, 0]]);
  const elementPage = new Map<string, string>();
  let currentPageId = FORM_START_PAGE_ID;
  let currentPageIndex = 0;
  elements.forEach(element => {
    if (element.type === 'PAGE_BREAK') {
      currentPageId = element.id;
      currentPageIndex += 1;
      pageOrder.set(currentPageId, currentPageIndex);
      return;
    }
    elementPage.set(element.id, currentPageId);
  });
  const pageGraph = new Map<string, string[]>(
    Array.from(pageIds).map(pageId => [pageId, []])
  );

  elements.forEach(element => {
    const dependencies: string[] = [];
    if (element.type === 'FILE_UPLOAD') {
      const maxFiles = element.config?.maxFiles;
      const maxFileSizeBytes = element.config?.maxFileSizeBytes;
      if (maxFiles && (!Number.isInteger(maxFiles) || maxFiles < 1 || maxFiles > 10)) {
        issues.push({ elementId: element.id, message: 'Maximum files must be between 1 and 10.' });
      }
      if (
        maxFileSizeBytes &&
        (!Number.isInteger(maxFileSizeBytes) || maxFileSizeBytes < 1 || maxFileSizeBytes > 50 * 1024 * 1024)
      ) {
        issues.push({ elementId: element.id, message: 'Maximum file size must be between 1 byte and 50 MB.' });
      }
      if (
        element.config?.allowedMimeTypes?.some(type => !/^[\w.+-]+\/[\w.+*-]+$/.test(String(type)))
      ) {
        issues.push({ elementId: element.id, message: 'One or more MIME types are invalid.' });
      }
    }
    if (['IMAGE', 'ATTACHMENT'].includes(element.type) && !String(element.config?.mediaId || element.config?.mediaKey || element.config?.mediaUrl || '').trim()) {
      issues.push({
        elementId: element.id,
        message: element.type === 'IMAGE'
          ? 'Upload an image or remove the image block.'
          : 'Upload a file or remove the attachment block.'
      });
    }
    if (element.kind === 'CONTENT' && element.type === 'URL') {
      const linkUrl = String(element.config?.linkUrl || '').trim();
      if (!linkUrl) {
        issues.push({ elementId: element.id, message: 'Add a destination URL or remove the link block.' });
      } else {
        try {
          new URL(linkUrl);
        } catch {
          issues.push({ elementId: element.id, message: 'Link destination must be a valid URL.' });
        }
      }
    }

    (element.visibility?.conditions || []).forEach(condition => {
      if (!elementIds.has(condition.fieldId)) {
        issues.push({ elementId: element.id, message: `Condition references a deleted element: ${condition.fieldId}` });
        return;
      }
      if (!questionIds.has(condition.fieldId)) {
        issues.push({ elementId: element.id, message: 'Conditions can only reference questions.' });
        return;
      }
      if (condition.fieldId === element.id) {
        issues.push({ elementId: element.id, message: 'An element cannot depend on its own answer.' });
        return;
      }
      dependencies.push(condition.fieldId);
    });

    graph.set(element.id, dependencies);
  });

  (definition.logic || []).forEach(rule => {
    if (!pageIds.has(rule.sourcePageId)) {
      issues.push({ message: `Flow rule references a deleted source page: ${rule.sourcePageId}` });
    }

    rule.when.conditions.forEach(condition => {
      if (!questionIds.has(condition.fieldId)) {
        issues.push({ message: `Flow rule references a deleted question: ${condition.fieldId}` });
        return;
      }
      const conditionPageId = elementPage.get(condition.fieldId) || FORM_START_PAGE_ID;
      if ((pageOrder.get(conditionPageId) || 0) > (pageOrder.get(rule.sourcePageId) || 0)) {
        issues.push({ message: 'A flow rule cannot depend on a question from a future page.' });
      }
    });

    if (rule.action.type === 'GO_TO_PAGE') {
      if (!pageIds.has(rule.action.targetId)) {
        issues.push({ message: `Flow rule references a deleted target page: ${rule.action.targetId}` });
      }
      if (rule.action.targetId === rule.sourcePageId) {
        issues.push({ message: 'A flow rule cannot transition to the same page.' });
      }
      if (
        (pageOrder.get(rule.action.targetId) || 0) <=
        (pageOrder.get(rule.sourcePageId) || 0)
      ) {
        issues.push({ message: 'A flow rule must transition to a later page.' });
      }
      if (
        pageIds.has(rule.sourcePageId) &&
        pageIds.has(rule.action.targetId) &&
        rule.action.targetId !== rule.sourcePageId
      ) {
        pageGraph.get(rule.sourcePageId)!.push(rule.action.targetId);
      }
    }

    if (rule.action.type === 'END_WITH_MESSAGE' && !rule.action.message.trim()) {
      issues.push({ message: 'End-with-message rules require a message.' });
    }
  });

  const visitingPages = new Set<string>();
  const visitedPages = new Set<string>();
  const visitPage = (pageId: string): boolean => {
    if (visitingPages.has(pageId)) return true;
    if (visitedPages.has(pageId)) return false;
    visitingPages.add(pageId);
    const hasCycle = (pageGraph.get(pageId) || []).some(visitPage);
    visitingPages.delete(pageId);
    visitedPages.add(pageId);
    return hasCycle;
  };
  if (Array.from(pageIds).some(visitPage)) {
    issues.push({ message: 'Page flow contains a cycle.' });
  }

  const visiting = new Set<string>();
  const visited = new Set<string>();

  const visit = (elementId: string): boolean => {
    if (visiting.has(elementId)) return true;
    if (visited.has(elementId)) return false;

    visiting.add(elementId);
    const hasCycle = (graph.get(elementId) || []).some(visit);
    visiting.delete(elementId);
    visited.add(elementId);
    return hasCycle;
  };

  elements.forEach(element => {
    if (visit(element.id) && !issues.some(issue => issue.message === 'Conditional visibility contains a cycle.')) {
      issues.push({ message: 'Conditional visibility contains a cycle.' });
    }
  });

  return issues;
};

export const removeElementAndReferences = (
  definition: FormDefinition,
  elementId: string
): FormDefinition => ({
  ...definition,
  elements: definition.elements
    .filter(element => element.id !== elementId)
    .map(element => {
      const conditions = (element.visibility?.conditions || []).filter(condition => condition.fieldId !== elementId);
      if (!element.visibility || conditions.length === element.visibility.conditions.length) return element;
      if (conditions.length === 0) {
        const { visibility, visibilityEffect, ...rest } = element;
        return rest as FormElement;
      }
      return {
        ...element,
        visibility: {
          ...element.visibility,
          conditions
        }
      };
    }),
  logic: (definition.logic || []).filter(rule => {
    if (rule.sourcePageId === elementId) return false;
    if ('targetId' in rule.action && rule.action.targetId === elementId) return false;
    return !rule.when.conditions.some(condition => condition.fieldId === elementId);
  })
});
